import type { ReactNode } from 'react';

import type { ErrorDoc, FieldDoc, ParamDoc } from '../../data/docs';

/**
 * Desktop: fixed-layout tables (long Type enums must not steal Description
 * width). Mobile (~640px): stacked key/value cards so names like
 * `itemLocation` never break mid-word into unreadable columns.
 */

type KvProps = {
    label: string;
    children: ReactNode;
    mono?: boolean;
};

function Kv({ label, children, mono }: KvProps) {
    return (
        <div className="grid grid-cols-[5.5rem_1fr] gap-x-3 gap-y-0.5 text-sm">
            <dt className="text-xs leading-5 text-text-muted">{label}</dt>
            <dd className={mono ? 'font-mono text-xs leading-5 break-words text-text' : 'leading-5 break-words text-text-muted'}>
                {children}
            </dd>
        </div>
    );
}

function MobileStack({ children }: { children: ReactNode }) {
    return <div className="space-y-2 sm:hidden">{children}</div>;
}

function MobileCard({ title, children }: { title: ReactNode; children: ReactNode }) {
    return (
        <div className="rounded-md border border-border px-3 py-3">
            <p className="font-mono text-xs font-medium break-words text-text">{title}</p>
            <dl className="mt-2 space-y-1.5">{children}</dl>
        </div>
    );
}

function DesktopTable({ children }: { children: ReactNode }) {
    return (
        <div className="hidden overflow-x-auto rounded-md border border-border sm:block">
            {children}
        </div>
    );
}

type ParamTableProps = {
    rows: ParamDoc[];
};

export function ParamTable({ rows }: ParamTableProps) {
    return (
        <>
            <MobileStack>
                {rows.map((row) => (
                    <MobileCard
                        key={row.name}
                        title={
                            <>
                                {row.name}
                                {row.required ? <span className="ml-1 text-text-muted">*</span> : null}
                            </>
                        }
                    >
                        <Kv label="Type" mono>
                            {row.type}
                        </Kv>
                        <Kv label="Default" mono>
                            {row.default ?? '—'}
                        </Kv>
                        <Kv label="Desc">{row.description}</Kv>
                    </MobileCard>
                ))}
            </MobileStack>

            <DesktopTable>
                {/* table-fixed so long Type enums cannot steal width from Description. */}
                <table className="w-full table-fixed border-collapse text-left text-sm">
                    <colgroup>
                        <col className="w-[18%]" />
                        <col className="w-[24%]" />
                        <col className="w-[14%]" />
                        <col />
                    </colgroup>
                    <thead>
                        <tr className="border-b border-border bg-bg-sunk text-xs text-text-muted">
                            <th className="px-3 py-2 font-medium">Name</th>
                            <th className="px-3 py-2 font-medium">Type</th>
                            <th className="px-3 py-2 font-medium">Default</th>
                            <th className="px-3 py-2 font-medium">Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.name} className="border-b border-border last:border-b-0 align-top">
                                <td className="px-3 py-2.5 font-mono text-xs break-words text-text">
                                    {row.name}
                                    {row.required ? <span className="ml-1 text-text-muted">*</span> : null}
                                </td>
                                <td className="px-3 py-2.5 font-mono text-xs break-words text-text-muted">
                                    {row.type}
                                </td>
                                <td className="px-3 py-2.5 font-mono text-xs break-words text-text-muted">
                                    {row.default ?? '-'}
                                </td>
                                <td className="px-3 py-2.5 text-text-muted">{row.description}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </DesktopTable>
        </>
    );
}

type FieldTableProps = {
    rows: FieldDoc[];
};

export function FieldTable({ rows }: FieldTableProps) {
    return (
        <>
            <MobileStack>
                {rows.map((row) => (
                    <MobileCard key={row.name} title={row.name}>
                        <Kv label="Type" mono>
                            {row.type}
                        </Kv>
                        <Kv label="Desc">{row.description}</Kv>
                    </MobileCard>
                ))}
            </MobileStack>

            <DesktopTable>
                <table className="w-full table-fixed border-collapse text-left text-sm">
                    <colgroup>
                        <col className="w-[22%]" />
                        <col className="w-[28%]" />
                        <col />
                    </colgroup>
                    <thead>
                        <tr className="border-b border-border bg-bg-sunk text-xs text-text-muted">
                            <th className="px-3 py-2 font-medium">Field</th>
                            <th className="px-3 py-2 font-medium">Type</th>
                            <th className="px-3 py-2 font-medium">Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.name} className="border-b border-border last:border-b-0 align-top">
                                <td className="px-3 py-2.5 font-mono text-xs break-words text-text">{row.name}</td>
                                <td className="px-3 py-2.5 font-mono text-xs break-words text-text-muted">
                                    {row.type}
                                </td>
                                <td className="px-3 py-2.5 text-text-muted">{row.description}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </DesktopTable>
        </>
    );
}

type ErrorTableProps = {
    rows: ErrorDoc[];
};

export function ErrorTable({ rows }: ErrorTableProps) {
    return (
        <>
            <MobileStack>
                {rows.map((row) => (
                    <MobileCard key={row.status} title={String(row.status)}>
                        <Kv label="Meaning">{row.meaning}</Kv>
                        <Kv label="When">{row.when}</Kv>
                    </MobileCard>
                ))}
            </MobileStack>

            <DesktopTable>
                <table className="w-full border-collapse text-left text-sm">
                    <thead>
                        <tr className="border-b border-border bg-bg-sunk text-xs text-text-muted">
                            <th className="px-3 py-2 font-medium">Status</th>
                            <th className="px-3 py-2 font-medium">Meaning</th>
                            <th className="px-3 py-2 font-medium">When</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.status} className="border-b border-border last:border-b-0 align-top">
                                <td className="px-3 py-2.5 font-mono text-xs text-text">{row.status}</td>
                                <td className="px-3 py-2.5 text-text">{row.meaning}</td>
                                <td className="px-3 py-2.5 text-text-muted">{row.when}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </DesktopTable>
        </>
    );
}
